/**
 * Windowing for the grouped session list (DesignSystem 4.4: lists virtualise above 100 rows).
 *
 * The groups flatten into rows (project header, session) of two uniform
 * heights, measured from the DOM once rendered; only the rows inside the scroll window plus
 * an overscan margin are mounted. At 10,000 sessions that is about 40 rows instead of
 * 10,000 (265,000 DOM nodes and a 1.4 GB heap before).
 */
import type { Session } from '@orgus/protocol';
import { useLayoutEffect, useState } from 'react';
import type { RefObject } from 'react';
import type { ProjectGroup } from './filters.ts';

/** Above this many sessions shown, the list is windowed. */
export const VIRTUAL_MIN_ROWS = 100;

export type VRow = { kind: 'project'; key: string; group: ProjectGroup } | { kind: 'session'; key: string; session: Session; project: number };

export type VKind = VRow['kind'];
export type RowHeights = Record<VKind, number>;

/** Starting guesses (a comfortable row), replaced by measured heights after the first render. */
export const ESTIMATED_HEIGHTS: RowHeights = { project: 40, session: 40 };

/**
 * Each project's header, then its sessions. `project` is the row index of the group's header,
 * or -1 when headers are left out (one project: a header would only repeat the page).
 */
export function flattenGroups(groups: readonly ProjectGroup[], headers = true): VRow[] {
  const rows: VRow[] = [];
  for (const g of groups) {
    const project = headers ? rows.length : -1;
    if (headers) rows.push({ kind: 'project', key: `p:${g.projectId}`, group: g });
    for (const s of g.sessions) rows.push({ kind: 'session', key: `s:${s.id}`, session: s, project });
  }
  return rows;
}

/**
 * New slot heights for the rows now mounted, or null when they already fit. Each kind's slot is
 * its tallest mounted row: exactly that the first time the kind is `measured` (replacing the
 * estimate), and only ever larger after that. Following one sample row instead made the height
 * flip between two values a pixel apart, each flip remounting a different sample, until React
 * gave up (error 185).
 */
export function fitSlots(heights: RowHeights, tallest: RowHeights, measured: Set<VKind>): RowHeights | null {
  let next: RowHeights | null = null;
  for (const kind of ['project', 'session'] as VKind[]) {
    if (tallest[kind] <= 0) continue;
    const slot = Math.ceil(tallest[kind]);
    const first = !measured.has(kind);
    measured.add(kind);
    if (first ? slot !== heights[kind] : slot > heights[kind]) next = { ...(next ?? heights), [kind]: slot };
  }
  return next;
}

/** Top offset of every row, and the total height. */
export function layoutRows(rows: readonly VRow[], h: RowHeights): { offsets: number[]; total: number } {
  const offsets = new Array<number>(rows.length);
  let y = 0;
  for (let i = 0; i < rows.length; i++) {
    offsets[i] = y;
    y += h[rows[i]!.kind];
  }
  return { offsets, total: y };
}

/** The last row starting at or before `y` (0 when y is above the list). */
export function rowAt(offsets: readonly number[], y: number): number {
  let lo = 0;
  let hi = offsets.length - 1;
  if (hi < 0) return 0;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (offsets[mid]! <= y) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

/** Rows to mount for the window [top, bottom): a half-open index range. */
export function visibleRange(offsets: readonly number[], top: number, bottom: number): [number, number] {
  if (!offsets.length || bottom <= top) return [0, 0];
  return [rowAt(offsets, top), Math.min(offsets.length, rowAt(offsets, bottom) + 1)];
}

/**
 * The row indexes to mount: the window [start, end), plus `keep` (the row holding keyboard
 * focus) when it is outside it, so scrolling a focused row away does not unmount it and drop
 * focus to the page body. In index order, so the DOM order matches the visual order.
 */
export function mountedRows(start: number, end: number, keep: number): number[] {
  const out: number[] = [];
  if (keep >= 0 && keep < start) out.push(keep);
  for (let i = start; i < end; i++) out.push(i);
  if (keep >= end) out.push(keep);
  return out;
}

/** The nearest ancestor that scrolls vertically, or the window. */
export function scrollParent(el: HTMLElement): HTMLElement | Window {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const oy = getComputedStyle(p).overflowY;
    if (oy === 'auto' || oy === 'scroll' || oy === 'overlay') return p;
  }
  return window;
}

export interface ScrollWindow {
  /** How far the viewport's top is below the element's top, in px (negative: the element starts lower). */
  top: number;
  height: number;
}

/**
 * Where the scroll viewport is, relative to `ref`'s top. Read at most once a frame, and only
 * re-rendered when it moved by `step` px or more (the caller's overscan covers the rest).
 */
export function useScrollWindow(ref: RefObject<HTMLElement | null>, step = 96): ScrollWindow {
  const [win, setWin] = useState<ScrollWindow>(() => ({ top: 0, height: typeof window === 'undefined' ? 900 : window.innerHeight }));
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const scroller = scrollParent(el);
    let frame = 0;
    const read = () => {
      frame = 0;
      const viewTop = scroller === window ? 0 : (scroller as HTMLElement).getBoundingClientRect().top;
      const height = scroller === window ? window.innerHeight : (scroller as HTMLElement).clientHeight;
      const top = viewTop - el.getBoundingClientRect().top;
      setWin((w) => (Math.abs(w.top - top) < step && w.height === height ? w : { top, height }));
    };
    const onChange = () => {
      if (!frame) frame = requestAnimationFrame(read);
    };
    read();
    scroller.addEventListener('scroll', onChange, { passive: true });
    window.addEventListener('resize', onChange);
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(onChange);
    ro?.observe(el);
    if (scroller !== window) ro?.observe(scroller as HTMLElement);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      scroller.removeEventListener('scroll', onChange);
      window.removeEventListener('resize', onChange);
      ro?.disconnect();
    };
  }, [ref, step]);
  return win;
}
